import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { useGetServiceDetailsConfig } from "api/queries/location/useGetServiceDetailsConfig";
import { GetServiceDetailsConfigResponseType } from "api/response/location.response";
import Divider from "components/v2/Divider/Divider";
import Radio from "components/v2/Radio/Radio";
import Textarea from "components/v2/Textarea/Textarea";
import FormSectionLabel from "@RequestFormV2/components/FormSectionLabel/FormSectionLabel";
import FieldErrorBadge from "@RequestFormV2/components/Badges/FieldErrorBadge";
import { useIsQuickRequest } from "@RequestFormV2/context/requestFormMode";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";
import { isEmptyValue } from "@RequestFormV2/validation/utils/validationMessages";
import { getHasMultipleCountries } from "@RequestFormV2/validation/getHasMultipleCountries";

type ClearanceType = "export" | "import" | "transit";

const CLEARANCE_DEFAULT_FIELD: Record<
  ClearanceType,
  keyof GetServiceDetailsConfigResponseType["service_options"]
> = {
  export: "export_clearance_default",
  transit: "transit_clearance_default",
  import: "import_clearance_default",
};

function getClearanceDefault(
  type: ClearanceType,
  serviceOptions: GetServiceDetailsConfigResponseType["service_options"] | undefined,
): string {
  const apiDefault = serviceOptions?.[CLEARANCE_DEFAULT_FIELD[type]];
  return typeof apiDefault === "string" && apiDefault ? apiDefault : "no";
}

const CLEARANCE_TYPES: {
  type: ClearanceType;
  labelId: string;
  defaultLabel: string;
  options: { value: string; labelId: string; defaultMessage: string }[];
  instructionsPlaceholderId: string;
  instructionsPlaceholderDefault: string;
}[] = [
  {
    type: "export",
    labelId: "new-request.service.exportClearanceNeeded",
    defaultLabel: "Export clearance needed?",
    options: [
      { value: "no", labelId: "new-request.service.noExportClearance", defaultMessage: "No, do not include Export clearance." },
      { value: "yes_provider", labelId: "new-request.service.yesExportClearance", defaultMessage: "Yes, please provide Export clearance." },
      { value: "yes_internal", labelId: "new-request.service.yesExportClearanceInternally", defaultMessage: "Yes, but it will be arranged internally." },
      { value: "yes_other", labelId: "new-request.service.other", defaultMessage: "Other" },
    ],
    instructionsPlaceholderId: "new-request.service.exportClearanceInstructionsPlaceholder",
    instructionsPlaceholderDefault: "Instructions for export clearance (e.g. Broker details, customs number, etc.)",
  },
  {
    type: "transit",
    labelId: "new-request.service.transitClearanceNeeded",
    defaultLabel: "Transit clearance needed?",
    options: [
      { value: "no", labelId: "new-request.service.noTransitClearance", defaultMessage: "No, do not include Transit clearance." },
      { value: "yes_provider", labelId: "new-request.service.yesTransitClearance", defaultMessage: "Yes, please provide Transit clearance." },
      { value: "yes_internal", labelId: "new-request.service.yesTransitClearanceInternally", defaultMessage: "Yes, but it will be arranged internally." },
      { value: "yes_other", labelId: "new-request.service.other", defaultMessage: "Other" },
    ],
    instructionsPlaceholderId: "new-request.service.transitClearanceInstructionsPlaceholder",
    instructionsPlaceholderDefault: "Instructions for transit clearance (e.g. Broker details, customs number, etc.)",
  },
  {
    type: "import",
    labelId: "new-request.service.importClearanceNeeded",
    defaultLabel: "Import clearance needed?",
    options: [
      { value: "no", labelId: "new-request.service.noImportClearance", defaultMessage: "No, do not include Import clearance." },
      { value: "yes_provider", labelId: "new-request.service.yesImportClearance", defaultMessage: "Yes, please provide Import clearance." },
      { value: "yes_internal", labelId: "new-request.service.yesImportClearanceInternally", defaultMessage: "Yes, but it will be arrange internally." },
      { value: "yes_other", labelId: "new-request.service.other", defaultMessage: "Other" },
    ],
    instructionsPlaceholderId: "new-request.service.importClearanceInstructionsPlaceholder",
    instructionsPlaceholderDefault: "Instructions for import clearance (e.g. Broker details, customs number, etc.)",
  },
];

function ClearanceField({
  config,
  defaultValue,
}: {
  config: (typeof CLEARANCE_TYPES)[number];
  defaultValue: string;
}) {
  const intl = useIntl();
  const { control, setValue } = useFormContext();

  const fieldName = `service.${config.type}_clearance`;
  const instructionsFieldName = `service.${config.type}_clearance_instructions`;
  const clearanceValue = useWatch({ name: fieldName });

  useReactiveReviewClear(fieldName, () => !isEmptyValue(clearanceValue), [clearanceValue]);

  const radioOptions = config.options.map((option) => ({
    value: option.value,
    label: intl.formatMessage({ id: option.labelId, defaultMessage: option.defaultMessage }),
  }));

  return (
    <div>
      <label className="required">
        {intl.formatMessage({ id: config.labelId, defaultMessage: config.defaultLabel })}
      </label>
      <Controller
        name={fieldName}
        control={control}
        shouldUnregister
        defaultValue={defaultValue}
        rules={{}}
        render={({ field, fieldState }) => (
          <>
            <Radio
              options={radioOptions}
              value={field.value}
              size="md"
              onChange={(value) => {
                field.onChange(value);
                if (value === "no") {
                  setValue(instructionsFieldName, "");
                }
              }}
            />
            {fieldState.error?.message && (
              <div className="mt-sm">
                <FieldErrorBadge message={String(fieldState.error.message)} />
              </div>
            )}
            {field.value && field.value !== "no" && (
              <div className="mt-md">
                <Textarea
                  name={instructionsFieldName}
                  shouldUnregister
                  placeholder={intl.formatMessage({
                    id: config.instructionsPlaceholderId,
                    defaultMessage: config.instructionsPlaceholderDefault,
                  })}
                  rows={2}
                />
              </div>
            )}
          </>
        )}
      />
    </div>
  );
}

export default function ClearanceSection() {
  const intl = useIntl();
  const { getValues, setValue } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();
  const isQuickRequest = useIsQuickRequest();
  const location = useWatch({ name: "organizational.location" });
  const stops: ScheduleStopValue[] | undefined = useWatch({ name: "schedule.stops" });
  const { data: serviceDetailsConfig } = useGetServiceDetailsConfig(location ?? "", {
    enabled: !!location,
  });

  const hasMultipleCountries = useMemo(() => getHasMultipleCountries(stops), [stops]);

  const serviceOptions = serviceDetailsConfig?.service_options;
  const showClearance =
    hasMultipleCountries && !!serviceOptions && (!isQuickRequest || !!serviceOptions.ask_clearance);

  useEffect(() => {
    if (!showClearance || !serviceOptions) return;

    CLEARANCE_TYPES.forEach(({ type }) => {
      const fieldName = `service.${type}_clearance`;
      if (getValues(fieldName)) return;

      const defaultValue = getClearanceDefault(type, serviceOptions);
      setValue(fieldName, defaultValue);
      saveContext?.saveField(fieldName, defaultValue).catch(() => undefined);
    });
  }, [showClearance, serviceOptions, getValues, setValue, saveContext]);

  if (!showClearance || !serviceOptions) return null;

  return (
    <>
      <Divider className="mb-3xl" />
      <div className="rfv2-form-section">
        <FormSectionLabel
          label={intl.formatMessage({
            id: "new-request.organisational-details.clearance",
            defaultMessage: "Clearance",
          })}
        />
        <div className="rfv2-form-section__content">
          <div className="d-flex flex-column gap-2xl">
            {CLEARANCE_TYPES.map((config) => (
              <ClearanceField
                key={config.type}
                config={config}
                defaultValue={getClearanceDefault(config.type, serviceOptions)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
